import axios from "axios";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL ||
  process.env.REACT_APP_API_URL ||
  window.location.origin;
const API_BASE = `${BACKEND_URL}/api`;

console.log("Initializing API client with backend URL:", BACKEND_URL);

const api = axios.create({
  baseURL: API_BASE,
  timeout: 25000,
  headers: {
    "Content-Type": "application/json",
  },
});

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Request interceptor to add token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    // Ensure token format is correct (without Bearer prefix if not already)
    const tokenValue = token.startsWith("bearer ") ? token : token;
    config.headers.Authorization = `Bearer ${tokenValue}`;
  }
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config || {};
    const method = (config.method || "").toLowerCase();
    const retries = config.__retryCount || 0;
    const isTimeout =
      error.code === "ECONNABORTED" ||
      (typeof error.message === "string" && error.message.includes("timeout"));
    const isNetworkError = !error.response;

    // Retry idempotent reads once for transient backend cold starts/network hiccups.
    if (method === "get" && retries < 1 && (isTimeout || isNetworkError)) {
      await sleep(800);
      return api.request({ ...config, __retryCount: retries + 1 });
    }

    if (error.response?.status === 401) {
      // Token expired or invalid - clear it
      localStorage.removeItem("token");
      window.location.href = "/admin/login";
    }
    return Promise.reject(error);
  },
);

export const authAPI = {
  register: (data) => api.post("/auth/register", data),
  login: (data) => api.post("/auth/login", data),
  verify: () => api.get("/auth/verify"),
};

export const articlesAPI = {
  getAll: (params) => api.get("/articles", { params }),
  getBySlug: (slug) => api.get(`/articles/slug/${slug}`),
  getById: (id) => api.get(`/articles/${id}`),
  search: (query, region) =>
    api.get("/articles/search", { params: { q: query, region } }),
  getByBrand: (brand, region, limit = 20) =>
    api.get("/articles", {
      params: { brand, region, is_published: true, limit },
    }),
  getByTag: (tag, region, limit = 20) =>
    api.get("/articles", {
      params: { tag, region, is_published: true, limit },
    }),
  getDeals: (region, limit = 20) =>
    api.get("/articles", {
      params: { has_deals: true, region, is_published: true, limit },
    }),
  getScheduled: () => api.get("/articles/scheduled"),
  create: (data) => api.post("/articles", data),
  update: (id, data) => api.put(`/articles/${id}`, data),
  delete: (id) => api.delete(`/articles/${id}`),
  bulkImport: (formData) =>
    api.post("/articles/bulk-import", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  export: (format) => api.get("/articles/export", { params: { format } }),
};

export const categoriesAPI = {
  getAll: (includeInactive = false) =>
    api.get("/categories", { params: { include_inactive: includeInactive } }),
  getById: (id) => api.get(`/categories/${id}`),
  getBySlug: (slug) => api.get(`/categories/slug/${slug}`),
  create: (data) => api.post("/categories", data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  deactivate: (id, reassignToCategoryId = null) =>
    api.post(`/categories/${id}/deactivate`, {
      reassign_to_category_id: reassignToCategoryId,
    }),
  activate: (id) => api.post(`/categories/${id}/activate`),
  getArticleCount: (id) => api.get(`/categories/${id}/articles-count`),
};

// pSEO APIs
export const pseoAPI = {
  // Unified pSEO page data
  getPageData: (pageType, slug, categorySlug, region) =>
    api.get("/pseo/page-data", {
      params: {
        page_type: pageType,
        slug,
        category_slug: categorySlug,
        region,
      },
    }),
  getAllSlugs: () => api.get("/pseo/all-slugs"),

  // Sale Events
  getSaleEvents: (isActive) =>
    api.get("/sale-events", { params: { is_active: isActive } }),
  getSaleEvent: (slug) => api.get(`/sale-events/${slug}`),
  createSaleEvent: (data) => api.post("/sale-events", data),
  updateSaleEvent: (id, data) => api.put(`/sale-events/${id}`, data),
  deleteSaleEvent: (id) => api.delete(`/sale-events/${id}`),

  // Brand SEO
  getBrands: (category, isPopular) =>
    api.get("/brands-seo", { params: { category, is_popular: isPopular } }),
  getBrand: (slug) => api.get(`/brands-seo/${slug}`),
  createBrand: (data) => api.post("/brands-seo", data),
  updateBrand: (id, data) => api.put(`/brands-seo/${id}`, data),
  deleteBrand: (id) => api.delete(`/brands-seo/${id}`),
};

export const commentsAPI = {
  getByArticle: (articleId) => api.get(`/comments/${articleId}`),
  create: (data) => api.post("/comments", data),
};

export const newsletterAPI = {
  subscribe: (data) => api.post("/newsletter", data),
};

// User Management APIs (Super Admin only)
export const usersAPI = {
  getAll: () => api.get("/admin/users"),
  create: (data) => api.post("/admin/users", data),
  update: (id, data) => api.put(`/admin/users/${id}`, data),
  delete: (id) => api.delete(`/admin/users/${id}`),
  getRoles: () => api.get("/admin/roles"),
  changePassword: (data) => api.post("/auth/change-password", data),
};

// Author Profile APIs
export const authorsAPI = {
  getAll: (activeOnly = true) =>
    api.get("/authors", { params: { active_only: activeOnly } }),
  getById: (id) => api.get(`/authors/${id}`),
  create: (data) => api.post("/authors", data),
  update: (id, data) => api.put(`/authors/${id}`, data),
  delete: (id) => api.delete(`/authors/${id}`),
};

// Brand Categories APIs (separate from article categories)
export const brandCategoriesAPI = {
  getAll: () => api.get("/brand-categories"),
  create: (data) => api.post("/brand-categories", data),
  update: (id, data) => api.put(`/brand-categories/${id}`, data),
  delete: (id) => api.delete(`/brand-categories/${id}`),
};

export const uploadAPI = {
  upload: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post("/upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },
};

export default api;
